import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';

import useService from '../../hooks/service.hook';
import useSnackbarHelper from '../../hooks/snackbar.hook';
import GroupsService from '../../services/groups.service';
import TeamsService from '../../services/teams.service';
import { Group, Team } from '../../models/training.models';
import { UserAvatar } from '../user-avatar/user-avatar.component';
import { palette } from '../../theme';

interface TpGroupMembersDialogProps {
  open: boolean;
  team: Team;
  group: Group | null;
  onClose: () => void;
  onRefresh: () => void;
}

const TpGroupMembersDialog: FC<TpGroupMembersDialogProps> = (props) => {
  const groupsService = useService(GroupsService);
  const teamsService = useService(TeamsService);
  const { showSnackbar } = useSnackbarHelper();

  const [groupName, setGroupName] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [members, setMembers] = useState<any[]>([]);
  const [allAthletes, setAllAthletes] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (props.open && props.group) {
      setGroupName(props.group.name);
      setMembers(props.group.members || []);
      setEditingName(false);
      setSearch('');
      teamsService
        .getAllTeamMembers(props.team.id as string)
        .then((response: any) => setAllAthletes(response.data.data || []))
        .catch(() => setAllAthletes([]));
    }
  }, [props.open, props.group]);

  const memberIds = useMemo(() => new Set(members.map((m) => String(m.id || m.userId))), [members]);

  const availableAthletes = useMemo(() => {
    const query = search.trim().toLowerCase();
    return allAthletes.filter((athlete) => {
      if (memberIds.has(String(athlete.id))) return false;
      if (!query) return true;
      return (athlete.name || '').toLowerCase().includes(query);
    });
  }, [allAthletes, memberIds, search]);

  const handleSaveName = () => {
    if (!props.group || !groupName.trim()) return;
    setLoading(true);
    groupsService
      .editGroup(props.team.id as string, props.group.id, groupName.trim())
      .then(() => {
        showSnackbar('Group name updated', 'success');
        setEditingName(false);
        props.onRefresh();
      })
      .catch(() => showSnackbar('Error updating group name', 'error'))
      .finally(() => setLoading(false));
  };

  const handleRemoveMember = (memberId: string | number) => {
    if (!props.group) return;
    setLoading(true);
    groupsService
      .removeMembersFromGroup(props.team.id as string, props.group.id, [memberId])
      .then((response: any) => {
        const updated = (response.data.data || []).find((g: Group) => String(g.id) === String(props.group?.id));
        setMembers(updated?.members || []);
        showSnackbar('Member removed', 'success');
        props.onRefresh();
      })
      .catch(() => showSnackbar('Error removing member', 'error'))
      .finally(() => setLoading(false));
  };

  const handleAddMember = (memberId: string | number) => {
    if (!props.group) return;
    setLoading(true);
    groupsService
      .addMembersToGroup(props.team.id as string, props.group.id, [memberId])
      .then((response: any) => {
        const updated = (response.data.data || []).find((g: Group) => String(g.id) === String(props.group?.id));
        setMembers(updated?.members || []);
        showSnackbar('Member added', 'success');
        props.onRefresh();
      })
      .catch((err) => {
        const message =
          err?.response?.status === 422 ? 'Team member is already a part of this group' : 'Error adding member';
        showSnackbar(message, 'error');
      })
      .finally(() => setLoading(false));
  };

  const handleDeleteGroup = () => {
    if (!props.group) return;
    if (!window.confirm(`Delete group "${props.group.name}"?`)) return;
    setLoading(true);
    groupsService
      .deleteGroup(props.team.id as string, props.group.id)
      .then(() => {
        showSnackbar('Group deleted', 'success');
        props.onRefresh();
        props.onClose();
      })
      .catch(() => showSnackbar('Error deleting group', 'error'))
      .finally(() => setLoading(false));
  };

  return (
    <Dialog open={props.open} onClose={props.onClose} fullWidth maxWidth="sm">
      <DialogTitle
        style={{
          background: `linear-gradient(135deg, ${palette.purple.dark}, ${palette.purple.main})`,
          color: palette.purple.contrastText,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          {editingName ? (
            <TextField
              size="small"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              onBlur={handleSaveName}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveName();
              }}
              autoFocus
              sx={{
                input: { color: '#fff', fontWeight: 700 },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.4)' },
              }}
            />
          ) : (
            <Typography variant="h6" style={{ cursor: 'pointer' }} onClick={() => setEditingName(true)}>
              {groupName}
            </Typography>
          )}
          <Typography variant="body2" style={{ opacity: 0.9 }}>
            {members.length} athlete{members.length === 1 ? '' : 's'}
          </Typography>
        </div>
        <IconButton onClick={props.onClose} style={{ color: '#fff' }} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          fullWidth
          size="small"
          placeholder="Search athletes to add…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ marginBottom: 12 }}
        />
        {availableAthletes.length > 0 && (
          <List dense style={{ maxHeight: 160, overflow: 'auto', marginBottom: 12 }}>
            {availableAthletes.slice(0, 8).map((athlete) => (
              <ListItem
                key={athlete.id}
                secondaryAction={
                  <Button size="small" onClick={() => handleAddMember(athlete.id)} disabled={loading}>
                    Add
                  </Button>
                }
              >
                <ListItemAvatar>
                  <UserAvatar name={athlete.name} src={athlete.profileImage} userId={athlete.id} size={32} />
                </ListItemAvatar>
                <ListItemText primary={athlete.name} secondary={athlete.email} />
              </ListItem>
            ))}
          </List>
        )}
        <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
          Members
        </Typography>
        <List dense>
          {members.map((member) => (
            <ListItem
              key={member.id || member.userId}
              secondaryAction={
                <IconButton
                  edge="end"
                  onClick={() => handleRemoveMember(member.id || member.userId)}
                  disabled={loading}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              }
            >
              <ListItemAvatar>
                <UserAvatar
                  name={member.name}
                  firstname={member.firstname}
                  lastname={member.lastname}
                  src={member.profileImage}
                  userId={member.id || member.userId}
                  size={32}
                />
              </ListItemAvatar>
              <ListItemText primary={member.name} secondary={member.email} />
            </ListItem>
          ))}
          {members.length === 0 && (
            <Typography variant="body2" color="textSecondary">
              No members in this group yet.
            </Typography>
          )}
        </List>
      </DialogContent>
      <DialogActions>
        <Button color="error" onClick={handleDeleteGroup} disabled={loading}>
          Delete Group
        </Button>
        <Button onClick={props.onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TpGroupMembersDialog;
